const DYNAMIC_IMPORT_ERROR_PATTERNS = [
	"Failed to fetch dynamically imported module",
	"Importing a module script failed",
	"error loading dynamically imported module",
	"ChunkLoadError",
];

/**
 * Route export names that TanStack Router's autoCodeSplitting splits into
 * lazy chunks. `lazyRouteComponent`/`lazyFn` read `res[exportName]` on the
 * import result — when a deploy-invalidated chunk URL resolves to
 * `undefined` (Vercel SPA fallback HTML), that property read throws a
 * TypeError whose message matches neither TanStack's isModuleNotFoundError
 * nor DYNAMIC_IMPORT_ERROR_PATTERNS (Sentry FARMASTER-FRONTEND-2S, issue
 * 127356226). The importer is router-generated code we cannot wrap with a
 * sentinel, so detection happens by message pattern — per browser wording,
 * scoped to these export names to avoid matching unrelated TypeErrors.
 */
const LAZY_ROUTE_EXPORT_NAMES = [
	// Plain `React.lazy(() => import(...))` on a default-export page: React
	// itself reads `payload._result.default` (lazyInitializer), so a stale
	// chunk resolving to `undefined` names 'default' in the message (Sentry
	// FARMASTER-FRONTEND-38, issue 133258487).
	"default",
	"component",
	"errorComponent",
	"pendingComponent",
	"notFoundComponent",
	"loader",
];

const LAZY_ROUTE_EXPORT_ERROR_PATTERNS = LAZY_ROUTE_EXPORT_NAMES.flatMap((name) => [
	`Cannot read properties of undefined (reading '${name}')`, // Chrome
	`can't access property "${name}"`, // Firefox
	// Safari includes the (minified) variable name, e.g.
	// "undefined is not an object (evaluating 'e.component')" — match only
	// the stable suffix.
	`.${name}')`, // Safari
]);

const ALL_CHUNK_ERROR_PATTERNS = [
	...DYNAMIC_IMPORT_ERROR_PATTERNS,
	...LAZY_ROUTE_EXPORT_ERROR_PATTERNS,
];

/**
 * Safari reports a COMPUTED member access on `undefined` as
 * "undefined is not an object (evaluating 'u[n]')" — bracket notation with
 * minified identifiers and NO stable export name. TanStack Router's
 * lazyRouteComponent/lazyFn read `res[exportName ?? "default"]` (computed,
 * not `res.component`), so a deploy-invalidated chunk that resolves to
 * `undefined` produces exactly this shape on Safari (Sentry
 * FARMASTER-FRONTEND-2W, issue 128005658). The minified names change every
 * build, so we match the structural `'<obj>[<idx>]'` form rather than a
 * literal `u[n]`. The bracket is load-bearing: it keeps this from matching
 * the dotted `evaluating 'res.data'` form (a genuine non-route bug); a real
 * bracket-on-undefined crash that did slip through only costs one
 * URL-loop-guarded reload, after which it surfaces normally.
 */
const SAFARI_COMPUTED_ACCESS_RE = /undefined is not an object \(evaluating '[^'[\]]*\[[^'\]]*\]'\)/;

function messageMatchesChunkError(message: string): boolean {
	return (
		ALL_CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern)) ||
		SAFARI_COMPUTED_ACCESS_RE.test(message)
	);
}

export function isDynamicImportChunkError(error: unknown): boolean {
	if (!error) return false;

	if (typeof error === "string") {
		return messageMatchesChunkError(error);
	}

	if (error instanceof Error) {
		return (
			messageMatchesChunkError(error.message) ||
			ALL_CHUNK_ERROR_PATTERNS.some((pattern) => error.name.includes(pattern))
		);
	}

	if (typeof error === "object") {
		const maybeMessage = Reflect.get(error, "message");
		const maybeName = Reflect.get(error, "name");

		if (typeof maybeMessage === "string") {
			return messageMatchesChunkError(maybeMessage);
		}

		if (typeof maybeName === "string") {
			return ALL_CHUNK_ERROR_PATTERNS.some((pattern) => maybeName.includes(pattern));
		}
	}

	return false;
}

/**
 * User-friendly message shown when a lazy chunk fails to load (typically
 * because a Vercel deploy invalidated the asset hash mid-session and the
 * SPA fallback returned `index.html` for the missing file). Stable copy
 * so all import call-sites speak with one voice.
 */
export const STALE_CHUNK_USER_MESSAGE =
	"Aplikacja została zaktualizowana. Odśwież stronę i spróbuj ponownie.";

/**
 * Translate a dynamic-import failure into a user-facing string.
 *
 * - Stale-chunk errors → {@link STALE_CHUNK_USER_MESSAGE} (actionable).
 * - Other errors → `${fallbackPrefix}: ${err.message}` (diagnostic).
 *
 * Centralizes copy so every XLSX/ExcelJS call-site reports stale chunks
 * the same way and we can iterate on the wording in one place.
 */
export function describeDynamicImportError(err: unknown, fallbackPrefix: string): string {
	if (isDynamicImportChunkError(err)) {
		return STALE_CHUNK_USER_MESSAGE;
	}
	const detail = err instanceof Error ? err.message : "Nieznany błąd";
	return `${fallbackPrefix}: ${detail}`;
}

/**
 * sessionStorage key guarding against reload loops on a single URL.
 * Lifted from main.tsx so call-sites can opt in to auto-reload without
 * waiting for the global `unhandledrejection` handler — when a per-call
 * try/catch swallows the rejection, that handler never fires.
 */
const RELOAD_MARKER_KEY = "fm:dynamic-import-reload";

/**
 * Module-level flag: a page reload has been scheduled in THIS page load.
 * `window.location.reload()` navigates asynchronously, so further chunk
 * errors can still fire before the page is replaced. They must be suppressed
 * rather than surfaced (FARMASTER-FRONTEND-2S residual). Resets to `false`
 * on the next page load via {@link clearStaleReloadMarker} (called at startup).
 */
let reloadScheduledThisLoad = false;

function currentUrlMarker(): string {
	return `${window.location.pathname}${window.location.search}`;
}

/**
 * Trigger a one-shot page reload, idempotent per `pathname+search`.
 *
 * - Returns `true` if a reload was scheduled (caller should stop further work).
 * - Returns `false` if the marker for this URL is already set (we already
 *   reloaded once on this URL — looping would burn CPU and never converge).
 * - Returns `false` in non-browser contexts (SSR/tests without `window`).
 */
export function reloadOnceAfterDynamicImportFailure(): boolean {
	if (typeof window === "undefined") return false;

	const reloadMarker = currentUrlMarker();
	const previousMarker = window.sessionStorage.getItem(RELOAD_MARKER_KEY);
	if (previousMarker === reloadMarker) return false;

	window.sessionStorage.setItem(RELOAD_MARKER_KEY, reloadMarker);
	reloadScheduledThisLoad = true;
	window.location.reload();
	return true;
}

/**
 * Drop the reload-loop marker when a successful navigation back to the
 * same URL has occurred. main.tsx calls this on startup so the next
 * stale-chunk failure on the same URL is allowed to reload again.
 */
export function clearStaleReloadMarker(): void {
	// A fresh page load means no reload from a previous incarnation is pending.
	reloadScheduledThisLoad = false;
	if (typeof window === "undefined") return;
	if (window.sessionStorage.getItem(RELOAD_MARKER_KEY) === currentUrlMarker()) {
		window.sessionStorage.removeItem(RELOAD_MARKER_KEY);
	}
}

/**
 * Per-callsite recovery: classify `err`, and when it's a stale-chunk
 * load failure, schedule a one-shot reload.
 *
 * - Returns `true` when recovery is in progress — caller should bail out
 *   silently (no toast/inline error: the page is about to reload).
 * - Returns `false` when the error wasn't a chunk error, or the
 *   loop-guard refused another reload — caller should fall back to
 *   `describeDynamicImportError(err, ...)` and surface the message.
 *
 * Why this lives next to a try/catch instead of relying on the global
 * `unhandledrejection` handler: when a callsite catches the rejection
 * (e.g. to show a toast), the global handler never sees it, so the
 * auto-reload optimisation is bypassed exactly when it would have
 * helped most.
 */
export function attemptDynamicImportRecovery(err: unknown): boolean {
	if (!isDynamicImportChunkError(err)) return false;
	// A reload is already scheduled in this page load — typically an earlier
	// lazy chunk on the same route already failed (component + layout + `intent`
	// prefetch invalidate together after a deploy). The page is about to be
	// replaced, so this second chunk error is not an actionable crash: report
	// recovery-in-progress (true) and stay silent, instead of falling through to
	// the per-URL loop guard's `false`, which made the ErrorBoundary report a
	// spurious TypeError to Sentry (FARMASTER-FRONTEND-2S residual leak).
	if (reloadScheduledThisLoad) return true;
	return reloadOnceAfterDynamicImportFailure();
}

/**
 * Sentinel error message: chunk-error pattern that
 * {@link isDynamicImportChunkError} matches. Throwing with this prefix
 * lets the caller's existing try/catch + `attemptDynamicImportRecovery`
 * path treat a structurally-broken module exactly like a failed import.
 */
const BROKEN_MODULE_PREFIX = "error loading dynamically imported module";

/**
 * Validate that a resolved `xlsx` module exposes the API surface our
 * call-sites use. Otherwise throw a chunk-error sentinel.
 *
 * Why: when Vercel's SPA fallback returns `index.html` (200 OK,
 * `Content-Type: text/html`) for a deploy-invalidated chunk URL,
 * Firefox 150 has been observed to parse the HTML into an EMPTY module
 * namespace instead of throwing. The subsequent `XLSX.read(...)` call
 * then crashes with `TypeError: F is undefined` (Sentry
 * FARMASTER-FRONTEND-1Q, issue 116574088), whose error message does
 * not match the chunk-error patterns and so escapes the recovery path.
 * Re-throwing as a sentinel routes it back through
 * `isDynamicImportChunkError` → `attemptDynamicImportRecovery`.
 */
export function ensureXlsxModule(mod: unknown): asserts mod is typeof import("xlsx") {
	const m = mod as { read?: unknown; utils?: unknown } | null | undefined;
	if (typeof m?.read !== "function" || !m?.utils) {
		throw new TypeError(`${BROKEN_MODULE_PREFIX}: xlsx (named exports missing)`);
	}
}

/** See {@link ensureXlsxModule}. Same idea, validating ExcelJS shape. */
export function ensureExcelJsModule(mod: unknown): asserts mod is typeof import("exceljs") {
	const m = mod as { Workbook?: unknown } | null | undefined;
	if (typeof m?.Workbook !== "function") {
		throw new TypeError(`${BROKEN_MODULE_PREFIX}: exceljs (named exports missing)`);
	}
}

/**
 * Unwrap a NAMED export from a dynamically imported module into the
 * `{ default }` shape that {@link React.lazy} expects, surviving the
 * stale-chunk-after-deploy failure mode.
 *
 * Why: `lazy(() => import("./Chart").then((m) => ({ default: m.Chart })))`
 * crashes with `TypeError: Cannot read properties of undefined (reading
 * 'Chart')` when Vercel's SPA fallback serves `index.html` for a
 * deploy-invalidated chunk URL and `m` resolves to `undefined` (Sentry
 * FARMASTER-FRONTEND-2H, issue 124532677). That message does not match
 * {@link isDynamicImportChunkError}, so the auto-reload recovery never
 * fires. This helper reaches the export with optional chaining and, when
 * the namespace or export is missing, throws the chunk-error sentinel that
 * routes the failure through the ErrorBoundary's recovery path.
 */
export function pickLazyComponent<T>(mod: unknown, exportName: string): { default: T } {
	const component = (mod as Record<string, T> | null | undefined)?.[exportName];
	if (component == null) {
		throw new TypeError(`${BROKEN_MODULE_PREFIX}: ${exportName} (named export missing)`);
	}
	return { default: component };
}

/**
 * Lazy-load the SheetJS `xlsx` module. Thin wrapper kept so call-sites
 * import from one place — easier to mock in tests, easier to grep, and
 * the natural seam if we later need retry/telemetry around the import.
 *
 * Includes a structural sanity check ({@link ensureXlsxModule}) that
 * converts a broken-module load (resolves OK but exports missing) into
 * a chunk-error sentinel routed through the standard recovery path.
 */
export async function loadXlsx(): Promise<typeof import("xlsx")> {
	const mod = await import("xlsx");
	ensureXlsxModule(mod);
	return mod;
}

/**
 * Pick `default` from a CJS-via-ESM interop module, or pass the namespace
 * through. Optional chaining is load-bearing — Firefox 150 has been
 * observed to resolve `await import(...)` to literal `undefined` when a
 * stale chunk URL returns Vercel's SPA-fallback HTML (Sentry
 * FARMASTER-FRONTEND-1S, issue 117651059). Without `?.` the unwrap
 * throws `TypeError: can't access property "default", X is undefined`,
 * whose message escapes {@link isDynamicImportChunkError}, defeating the
 * auto-reload recovery. Letting `undefined` propagate to the
 * shape-validating guard ({@link ensureExcelJsModule}) routes the
 * failure through the sentinel path instead.
 */
export function unwrapEsmDefault<T>(raw: T | { default?: T } | null | undefined): T | undefined {
	if (raw == null) return undefined;
	const withDefault = raw as { default?: T };
	return withDefault.default ?? (raw as T);
}

/**
 * Lazy-load the `exceljs` module. Same rationale as {@link loadXlsx}.
 *
 * ExcelJS ships as a CJS package (`main: "./excel.js"`) with named-export
 * type declarations. ESM-importing a CJS module in Vite/Rollup wraps the
 * CJS exports under a `default` property at runtime, but the d.ts has no
 * `default` member, so the call-site historically did
 * `(await import("exceljs")).default` and relied on a TypeScript
 * tolerance. We hide that interop quirk in {@link unwrapEsmDefault}: the
 * public signature returns the named-export module shape that matches
 * the d.ts (`Workbook` etc.), while at runtime we still pick the
 * `.default` payload first when present, and survive the FF150
 * undefined-namespace quirk.
 */
export async function loadExcelJs(): Promise<typeof import("exceljs")> {
	const raw = (await import("exceljs")) as unknown as
		| ({ default?: typeof import("exceljs") } & typeof import("exceljs"))
		| undefined;
	const mod = unwrapEsmDefault<typeof import("exceljs")>(raw);
	ensureExcelJsModule(mod);
	return mod;
}
